import { useState } from 'react';
import { Check } from 'lucide-react';
import { useAppStore } from '@/store/store';
import { selectShipmentSummary } from '@/store/selectors';
import { HistoryList } from '@/components/History/HistoryList';
import { AnalyticsDashboard } from '@/components/History/AnalyticsDashboard';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';

interface HistoryScreenProps {
  onErrorReport?: () => void;
}

export function HistoryScreen({ onErrorReport }: HistoryScreenProps) {
  const saveCalculation = useAppStore((s) => s.saveCalculation);
  const [saveName, setSaveName] = useState('');
  const [showSave, setShowSave] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    if (!saveName.trim()) return;
    const state = useAppStore.getState();
    const summary = selectShipmentSummary(state);
    saveCalculation(saveName.trim(), {
      packages: state.packages,
      mode: state.mode,
      dimDivisor: state.dimDivisor,
      overrideDivisor: state.divisorOverridden,
    }, summary);
    setSaveName('');
    setShowSave(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
        <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-bold)' }}>
          Calculation History
        </h2>
        {saved ? (
          <span
            role="status"
            aria-live="polite"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-1)',
              padding: 'var(--space-1) var(--space-3)',
              borderRadius: 'var(--radius-full)',
              background: 'var(--color-success-light)',
              color: 'var(--color-success)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semibold)',
              minHeight: 'var(--input-height)',
            }}
          >
            <Check size={14} /> Saved
          </span>
        ) : (
          <button className="btn btn-primary btn-sm" onClick={() => setShowSave(true)}>
            Save Current
          </button>
        )}
      </div>

      {showSave && (
        <div className="card" style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Calculation name..."
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSave()}
            aria-label="Calculation name"
            style={{
              flex: 1,
              padding: 'var(--space-2) var(--space-3)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border)',
              background: 'var(--color-bg-primary)',
              color: 'var(--color-text-primary)',
              fontSize: 'var(--font-size-sm)',
            }}
            autoFocus
          />
          <button className="btn btn-primary btn-sm" onClick={handleSave}>Save</button>
          <button className="btn btn-sm" onClick={() => setShowSave(false)}>Cancel</button>
        </div>
      )}

      <ErrorBoundary onReportError={onErrorReport}>
        <HistoryList />
      </ErrorBoundary>
      <ErrorBoundary onReportError={onErrorReport}>
        <AnalyticsDashboard />
      </ErrorBoundary>
    </div>
  );
}
